/**
 * Registry of built-in computed functions for dashboard expressions.
 */

export type ComputedFunction = (args: Record<string, unknown>) => unknown

const registry = new Map<string, ComputedFunction>()

export function registerComputedFunction(name: string, fn: ComputedFunction): void {
  registry.set(name, fn)
}

export function getComputedFunction(name: string): ComputedFunction | undefined {
  return registry.get(name)
}

export function getAllComputedFunctions(): Record<string, ComputedFunction> {
  return Object.fromEntries(registry)
}

// ─── Built-in Functions ─────────────────────────────────────────────

registerComputedFunction("formatCurrency", ({ value, currency }) => {
  const num = Number(value ?? 0)
  const curr = typeof currency === "string" ? currency : "USD"
  try {
    return new Intl.NumberFormat("en-US", { style: "currency", currency: curr }).format(num)
  } catch {
    return `$${num.toFixed(2)}`
  }
})

registerComputedFunction("formatPercent", ({ value, decimals }) => {
  const num = Number(value ?? 0)
  const dec = typeof decimals === "number" ? decimals : 1
  return `${(num * 100).toFixed(dec)}%`
})

registerComputedFunction("formatNumber", ({ value, decimals }) => {
  const num = Number(value ?? 0)
  const dec = typeof decimals === "number" ? decimals : 0
  return new Intl.NumberFormat("en-US", {
    minimumFractionDigits: dec,
    maximumFractionDigits: dec,
  }).format(num)
})

registerComputedFunction("sum", ({ array, key }) => {
  if (!Array.isArray(array)) return 0
  return array.reduce((acc: number, item) => {
    const val =
      key && typeof item === "object" && item !== null
        ? (item as Record<string, unknown>)[key as string]
        : item
    return acc + Number(val ?? 0)
  }, 0)
})

registerComputedFunction("count", ({ array }) => {
  return Array.isArray(array) ? array.length : 0
})

registerComputedFunction("avg", ({ array, key }) => {
  if (!Array.isArray(array) || array.length === 0) return 0
  const total = array.reduce((acc: number, item) => {
    const val =
      key && typeof item === "object" && item !== null
        ? (item as Record<string, unknown>)[key as string]
        : item
    return acc + Number(val ?? 0)
  }, 0)
  return total / array.length
})
